import { Box } from '@mui/material';
import { useCaseSchedulePaginated, useUpdateDispatchStatus } from '@peritasai/repository';
import { ErrorCard } from '@peritasai/ui-components';
import { useNavigate } from '@tanstack/react-router';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentTime } from '../hooks/useCurrentTime';
import { readStoredDate, writeStoredDate } from '../utils/dateStorage';
import { splitIntoQueues } from '../utils/queues';
import { CaseCartAssemblyCard } from './CaseCartAssemblyCard';
import { LoadingCard } from './LoadingCard';
import {
  contentWrapVariantSx,
  pageContainerVariantSx,
  popupOverlaySx,
} from './styles';

const ASSEMBLY_PAGE_SIZE = 100;

export const CaseCartAssemblyPage = () => {
  const { t } = useTranslation(['periverse', 'common']);
  const navigate = useNavigate();
  // Persisted across the session (not just component state) so navigating
  // into a case's progress screen and back doesn't reset the date chip to
  // Today — the cart builder's chosen day should survive the round trip.
  const [selectedDate, setSelectedDateState] = useState<Dayjs>(() => {
    const stored = readStoredDate();
    const today = dayjs().startOf('day');
    return stored && !stored.isBefore(today, 'day') ? stored : today;
  });
  const setSelectedDate = useCallback((date: Dayjs) => {
    writeStoredDate(date);
    setSelectedDateState(date);
  }, []);
  const now = useCurrentTime(60_000);

  const dateRange = useMemo(
    () => ({
      startDate: selectedDate.startOf('day').toISOString(),
      endDate: selectedDate.endOf('day').toISOString(),
    }),
    [selectedDate]
  );

  const { data, isLoading, isError, refetch } = useCaseSchedulePaginated(
    dateRange,
    1,
    ASSEMBLY_PAGE_SIZE,
    { enableRealtime: true }
  );

  const [prevIsError, setPrevIsError] = useState(isError);
  const [isErrorDismissed, setIsErrorDismissed] = useState(false);
  if (isError !== prevIsError) {
    setPrevIsError(isError);
    if (isError) setIsErrorDismissed(false);
  }

  const { readyToBuild, readyForDispatch, arrivedInOr } = useMemo(
    () => splitIntoQueues(data?.cases ?? []),
    [data?.cases]
  );

  const handleSelect = useCallback(
    (caseId: string) => {
      navigate({ to: '/case-cart-assembly-detail', search: { caseId } });
    },
    [navigate]
  );

  const updateDispatchStatus = useUpdateDispatchStatus();
  const sendingToOrCaseId = updateDispatchStatus.isPending
    ? (updateDispatchStatus.variables?.caseId ?? null)
    : null;

  const handleSendToOr = useCallback(
    (caseId: string) => {
      updateDispatchStatus.mutate({ caseId, dispatchStatus: true });
    },
    [updateDispatchStatus]
  );

  return (
    <Box sx={{ ...pageContainerVariantSx(), py: { xs: '2rem', md: '24px', lg: '24px' } }}>
      <Box sx={contentWrapVariantSx()}>
        {isLoading && (
          <LoadingCard
            rows={5}
            titleWidth="35%"
            cardWidth="100%"
            cardMaxWidth="100%"
          />
        )}

        {!isLoading && isError && !isErrorDismissed && (
          <Box sx={popupOverlaySx}>
            <ErrorCard
              title={t('pages.buildCart.caseList.errorTitle')}
              description={t('pages.buildCart.caseList.errorDescription')}
              retryLabel={t('common:tryAgain')}
              onRetry={() => void refetch()}
              cancelLabel={t('common:cancel')}
              onCancel={() => setIsErrorDismissed(true)}
            />
          </Box>
        )}

        {!isLoading && (!isError || isErrorDismissed) && (
          <CaseCartAssemblyCard
            readyToBuild={readyToBuild}
            readyForDispatch={readyForDispatch}
            arrivedInOr={arrivedInOr}
            now={now}
            selectedDate={selectedDate}
            onSelectDate={setSelectedDate}
            onSelect={handleSelect}
            onSendToOr={handleSendToOr}
            sendingToOrCaseId={sendingToOrCaseId}
          />
        )}
      </Box>
    </Box>
  );
};
